import { TFunction } from 'i18next';
import { Lock, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import { minutesToHours } from '@/helpers';
import { cn } from '@/lib/utils';
import { formatWallClockDate, formatWallClockTime } from '@/utils/date-formatters';

import { TimeTrack } from '../types/time-tracking';

interface TimeTrackEntryProps {
  track: TimeTrack;
  onEdit: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onDelete: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isEditVisible: boolean;
  isDeleteVisible: boolean;
  /** When true, omits the small left vertical bar that signals approved/unapproved status. */
  hideStatusIndicator?: boolean;
  /**
   * Optional classes applied to the hours-badge background, the badge's
   * group-hover background, and the edit/delete buttons' hover background.
   * All three must be statically-known Tailwind classes (no template-literal
   * composition) so the JIT can extract them. Used by surfaces where the
   * default `bg-muted` blends into a pale parent container — the week-view
   * DayGroup cards use a slightly darker tone to keep the chips legible.
   */
  accentBgClassName?: string;
  accentBgGroupHoverClassName?: string;
  accentBgHoverClassName?: string;
}

const formatTrackedHours = (trackedHours: number, t: TFunction) => {
  const totalMinutes = Math.round(trackedHours * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${t('entry.hours', { hours })} ${t('entry.minutes', { minutes })}`;
};

export function TimeTrackEntry({
  track,
  onEdit,
  onDelete,
  isEditVisible,
  isDeleteVisible,
  hideStatusIndicator = false,
  accentBgClassName,
  accentBgGroupHoverClassName,
  accentBgHoverClassName,
}: TimeTrackEntryProps) {
  const badgeBgClass = accentBgClassName ?? 'bg-muted';
  const badgeGroupHoverClass = accentBgGroupHoverClassName ?? 'group-hover/entry:bg-muted';
  const buttonHoverClass = accentBgHoverClassName ?? 'hover:bg-muted';
  const { t } = useTranslation('authenticated.timeTracking');

  const trackedHours = minutesToHours(track.trackedMinutes);

  return (
    <div className="group/entry rounded-md px-4 py-2 hover:bg-muted/50">
      <div className="flex items-center gap-4">
        {!hideStatusIndicator && (
          <div
            className={cn(
              'h-[36px] w-[2px] shrink-0',
              track.isApproved ? 'bg-green-500' : 'bg-muted'
            )}
          />
        )}

        {/* Date and time */}
        <div className="flex shrink-0 flex-col">
          <span className="text-xs font-medium md:text-sm">{formatWallClockDate(track.date)}</span>
          <span className="text-xs text-muted-foreground">{formatWallClockTime(track.date)}</span>
        </div>

        {/* Description — hidden on small screens, shown inline on md+ */}
        <div className="hidden flex-1 md:block">
          <TruncatedTooltip
            content={track.description || t('entry.noDescription')}
            maxLines={2}
            preserveWhitespace
            className={cn('text-sm', !track.description && 'text-muted-foreground')}
          />
        </div>

        {/* Tracked hours badge — defaults to `bg-muted` (matches the left
            status indicator strip). Surfaces with a pale wrapper override the
            tone via the accent props so the chip stays legible. */}
        <Badge
          variant="secondary"
          className={cn(
            'ml-auto shrink-0 justify-center rounded-md p-3 md:ml-0 md:min-w-[100px]',
            badgeBgClass,
            badgeGroupHoverClass
          )}
        >
          <span className="text-sm">{formatTrackedHours(trackedHours, t)}</span>
        </Badge>

        {/* Actions — hover background matches the badge so the row's icon
            buttons sit on the same neutral surface. */}
        <div className="flex shrink-0 gap-1">
          {track.isApproved ? (
            <SimpleTooltip content={t('entry.approvedTooltip')}>
              <Button variant="ghost" size="icon" className="cursor-default" tabIndex={-1}>
                <Lock className="h-4 w-4 text-green-600 dark:text-green-400" />
              </Button>
            </SimpleTooltip>
          ) : (
            isEditVisible && (
              <Button variant="ghost" size="icon" onClick={onEdit} className={buttonHoverClass}>
                <Pencil className="h-4 w-4" />
              </Button>
            )
          )}
          {isDeleteVisible && !track.isApproved && (
            <Button variant="ghost" size="icon" onClick={onDelete} className={buttonHoverClass}>
              <Trash2 className="h-4 w-4" color="red" />
            </Button>
          )}
        </div>
      </div>

      {/* Description — shown below on small screens */}
      <div className="mt-1 pl-6 md:hidden">
        <TruncatedTooltip
          content={track.description || t('entry.noDescription')}
          maxLines={2}
          className={cn('text-xs', !track.description && 'text-muted-foreground')}
        />
      </div>
    </div>
  );
}
